import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Container,
  Paper,
  Typography,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Divider,
  Menu,
  MenuItem,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { useNavigate } from 'react-router-dom';
import SuccessMessage from '../../../components/successMessage';
import ErrorMessage from '../../../components/errorMessage';
import Loading from '../../../components/loading';
import CovenantFormModal from '../covenantFormModal';
import { useCovenantCatalog } from '../../../hooks/useCovenantCatalog';
import { useDeleteCovenant } from '../hooks/useDeleteCovenant';

function CovenantListWeb() {
  const navigate = useNavigate();

  const { covenants, isLoading, error: listError, refetch: getCovenants } = useCovenantCatalog();
  const { remove } = useDeleteCovenant();

  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    if (listError) {
      setShowErrorMessage(true);
      setErrorMessage(listError);
    }
  }, [listError]);

  const [isModalOpen, setModalOpen] = useState(false);
  const [isConfirmModalOpen, setConfirmModalOpen] = useState(false);
  const [selectedCovenant, setSelectedCovenant] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedCovenantId, setSelectedCovenantId] = useState(null);
  const isMenuOpen = Boolean(anchorEl);

  const clickMenu = (event, covenantId) => {
    setAnchorEl(event.currentTarget);
    setSelectedCovenantId(covenantId);
  };

  const closeMenu = () => setAnchorEl(null);

  const openCreateModal = () => {
    setSelectedCovenant(null);
    setModalOpen(true);
  };

  const openEditModal = () => {
    const covenant = covenants.find((item) => item.id === selectedCovenantId);
    if (covenant) {
      setSelectedCovenant(covenant);
      setModalOpen(true);
    }
    closeMenu();
  };

  const closeModal = () => {
    setModalOpen(false);
    setSelectedCovenant(null);
  };

  const openConfirmModal = () => {
    setAnchorEl(null);
    setConfirmModalOpen(true);
  };

  const closeConfirmModal = () => {
    setConfirmModalOpen(false);
    setSelectedCovenantId(null);
  };

  const handleCovenantSaved = (savedCovenant, message) => {
    getCovenants();
    setSuccessMessage(message ?? 'Convênio salvo com sucesso.');
    setShowSuccessMessage(true);
  };

  const confirmDelete = () => {
    setConfirmModalOpen(false);
    remove(selectedCovenantId)
      .then((returnFromApi) => {
        getCovenants();
        setSuccessMessage(returnFromApi.data ?? 'Convênio excluído com sucesso.');
        setShowSuccessMessage(true);
      })
      .catch((returnFromApi) => {
        setShowErrorMessage(true);
        setErrorMessage(returnFromApi.response?.data ?? 'Erro ao excluir convênio.');
        console.error('ops! ocorreu um erro' + returnFromApi);
        if (returnFromApi.response?.status === 401) navigate('/expired-session');
      })
      .finally(() => {
        setSelectedCovenantId(null);
      });
  };

  return (
    <>
      <Loading isToHidden={isLoading === false} />
      {showSuccessMessage && (
        <SuccessMessage
          message={successMessage}
          show={showSuccessMessage}
          setShow={setShowSuccessMessage}
        />
      )}
      {showErrorMessage && (
        <ErrorMessage
          message={errorMessage}
          show={showErrorMessage}
          setShow={setShowErrorMessage}
        />
      )}

      <Box sx={{ p: 4 }}>
        <Container maxWidth="lg">
          <Paper elevation={0} variant="outlined" sx={{ p: 4, borderRadius: '12px' }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 3,
              }}
            >
              <Typography variant="h5" component="h1" sx={{ fontWeight: '600' }}>
                Convênios
              </Typography>
              <Button variant="outlined" onClick={openCreateModal} data-cy="add-covenant-btn">
                Adicionar Convênio
              </Button>
            </Box>

            <List>
              {covenants.length === 0 && !isLoading ? (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ py: 2, textAlign: 'center' }}
                >
                  Nenhum convênio cadastrado
                </Typography>
              ) : (
                covenants.map((covenant) => (
                  <React.Fragment key={covenant.id}>
                    <ListItem
                      secondaryAction={
                        <IconButton onClick={(event) => clickMenu(event, covenant.id)}>
                          <MoreVertIcon />
                        </IconButton>
                      }
                    >
                      <ListItemText
                        primary={covenant.name}
                        primaryTypographyProps={{ fontWeight: '600' }}
                      />
                    </ListItem>
                    <Divider component="li" />
                  </React.Fragment>
                ))
              )}
            </List>
          </Paper>
        </Container>

        <Menu anchorEl={anchorEl} open={isMenuOpen} onClose={closeMenu}>
          <MenuItem onClick={openEditModal}>Editar</MenuItem>
          <MenuItem onClick={openConfirmModal} sx={{ color: 'error.main' }}>
            Excluir
          </MenuItem>
        </Menu>

        <CovenantFormModal
          open={isModalOpen}
          onClose={closeModal}
          onSave={handleCovenantSaved}
          covenant={selectedCovenant}
        />

        <Dialog open={isConfirmModalOpen} onClose={closeConfirmModal}>
          <DialogTitle sx={{ fontWeight: 'bold' }}>Confirmar Exclusão</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Você tem certeza que deseja excluir este convênio? Ele deixará de aparecer nas listas
              de seleção do sistema.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={closeConfirmModal}>Cancelar</Button>
            <Button onClick={confirmDelete} variant="contained" color="error" autoFocus>
              Excluir
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </>
  );
}

export default CovenantListWeb;
